import { getLocale, getTimeZone } from './helpers';
import config from './config';

// Caches for formatters
const cache = {
    int: null,
    percent: null,
    currency: {},
    currencyBalance: {},
    date: {},
};

/**
 * Lightweight currency formatter used when Intl.NumberFormat throws
 * (e.g., unknown currency like BTC, XBT, MATRIX).
 */
function createFallbackCurrencyFormatter(locale, opts = {}, currency) {
    const min = opts.minimumFractionDigits ?? 2;
    const max = opts.maximumFractionDigits ?? 2;

    // optional symbol/placement overrides from config
    const symbol =
        (config.currencySymbols && config.currencySymbols[currency]) || currency;
    const placement =
        (config.currencyPlacement && config.currencyPlacement[currency]) || 'prefix'; // 'prefix'|'suffix'

    // Use Intl just for number grouping/decimals
    const nf = new Intl.NumberFormat(locale, {
        minimumFractionDigits: min,
        maximumFractionDigits: max,
        useGrouping: true,
    });

    return {
        format(value) {
            const amount = nf.format(value);
            return placement === 'suffix' ? `${amount} ${symbol}` : `${symbol} ${amount}`;
        },
    };
}

/** Safe NumberFormat — never throws, returns an object with .format(...) */
function safeNumberFormat(locale, opts = {}, { forCurrency = false } = {}) {
    try {
        return new Intl.NumberFormat(locale, opts);
    } catch {
        if (forCurrency) {
            return createFallbackCurrencyFormatter(locale, opts, opts.currency);
        }
        // plain number fallback
        const nf = new Intl.NumberFormat(locale, {
            minimumFractionDigits: opts.minimumFractionDigits ?? 0,
            maximumFractionDigits: opts.maximumFractionDigits ?? 0,
            useGrouping: true,
        });
        return { format: (n) => nf.format(n) };
    }
}

/** Safe DateTimeFormat — if TZ is bad, drop it to avoid runtime errors */
function safeDateTimeFormat(locale, opts = {}) {
    try {
        return new Intl.DateTimeFormat(locale, opts);
    } catch {
        const { timeZone, ...rest } = opts;
        return new Intl.DateTimeFormat(locale, rest);
    }
}

const Self = {
    getCurrency(currency) {
        if (cache.currency[currency]) return cache.currency[currency];

        // Merge defaults with per-currency overrides (if present)
        const options = {
            ...(config.currency?.default || {}),
            ...(config.currency?.[currency] || {}),
            style: 'currency',
            currency,
        };

        const fmt = safeNumberFormat(getLocale(), options, { forCurrency: true });
        cache.currency[currency] = fmt;
        return fmt;
    },

    getCurrencyBalance(currency) {
        if (cache.currencyBalance[currency]) return cache.currencyBalance[currency];

        const options = {
            ...(config.currencyBalance?.default || {}),
            ...(config.currencyBalance?.[currency] || {}),
            style: 'currency',
            currency,
        };

        const fmt = safeNumberFormat(getLocale(), options, { forCurrency: true });
        cache.currencyBalance[currency] = fmt;
        return fmt;
    },

    getNumber(key, params) {
        const locale = getLocale();

        // ad-hoc formatter on demand
        if (params) {
            return safeNumberFormat(locale, params);
        }

        // or cached named formatter from config
        if (!cache[key]) {
            cache[key] = safeNumberFormat(locale, config[key] || {});
        }
        return cache[key];
    },

    getDate(key, timeZone = getTimeZone()) {
        const k = `${key}-${timeZone}`;
        if (!cache.date[k]) {
            const options = { ...(config[key] || {}), timeZone };
            cache.date[k] = safeDateTimeFormat(getLocale(), options);
        }
        return cache.date[k];
    },
};

export default Self;
